import { injectable, inject } from 'inversify';
import Debug from 'debug';
import { DualCommandQueue }from '../../shared/dual-command-queue'
import * as Commands from './commands'
import { StoreSymbol } from '@/store';
import { Store } from 'vuex';
import { SectionStateStoreModuleSymbol, SectionStateStoreModule } from '@/app/section-state';
import { ResettableCancelToken, compoundCancelToken, CancelToken } from '@/app/shared/utils/cancel-token'
import { BadgeUri } from './commands';

export const BadgeServiceSymbol = Symbol.for('BadgeService');

type BadgeSource = Commands.BadgeUri | null;

const debug = Debug('badge-service');

@injectable()
export class BadgeService {

  private queue: DualCommandQueue;
  private cancelToken: ResettableCancelToken

  private unwatchSectionState: () => void;

  constructor(
    @inject(SectionStateStoreModuleSymbol)
    private sectionStateStore: SectionStateStoreModule,
    @inject(StoreSymbol)
    private store: Store<unknown>,
    @inject(Commands.LoadBadgesCommand)
    private loadBadgesCommand: Commands.LoadBadgesCommand,
    @inject(Commands.QueryBadgesTrackingStateCommand)
    private queryBadgesStateCommand: Commands.QueryBadgesTrackingStateCommand,
    @inject(Commands.CalculateBadgesCommand)
    private calculateBadgesCommand: Commands.CalculateBadgesCommand) {

    this.queue = new DualCommandQueue('badge-service')
    this.cancelToken = new ResettableCancelToken()
  }

  public start(source: BadgeSource) {
    debug('starting BadgeService', source);

    this.queue.start()

    if (source) {
      const token = this.cancelToken.cancelAndReset()
      this.queue.enqueueLocal('load-badges', (queueCancel) => this.load(source, token, queueCancel))
      this.queue.enqueueLocal('calculate-loaded-badges', () => this.calculateBadgesCommand.execute());
    }
    this.queue.enqueueRemote('query-tracking-state', (cancel) => this.queryBadgesStateCommand.execute(cancel));

    this.watch();
  }

  public reset(source: BadgeSource) {
    debug('resetting BadgeService', source);

    if (source) {
      const token = this.cancelToken.cancelAndReset()
      this.queue.enqueueLocal('reload-badges', (queueCancel) => this.load(source, token, queueCancel))
      this.queue.enqueueLocal('calculate-reloaded-badges', () => this.calculateBadgesCommand.execute());
    }
  }

  private async load(source: BadgeUri, token: CancelToken, queueCancel: CancelToken) {
    await this.loadBadgesCommand.execute(source, compoundCancelToken(token, queueCancel))
  }

  public async stop() {

    this.cancelToken.cancelAndReset()
    this.unwatch();
    await this.queue.stop()
  }

  private watch() {
    this.unwatchSectionState = this.store.watch(() => this.sectionStateStore.states, (value, oldValue) => {
      debug('section state changed');

      this.calculateBadgesCommand.execute();
    }, {
      deep: true
    });
  }

  private unwatch() {
    this.unwatchSectionState();
  }
}
